import { supabase } from '@/shared/lib/supabase';

/**
 * announcementService (W5, 2026-08-11 / GA-D3).
 *
 * Active-window visibility is enforced by RLS (announcement_select_active —
 * expired rows vanish with no cron); audience is filtered here as a UI
 * concern. Writes are full-staff-only at the RLS layer; the blast goes
 * through the announcement-send EF (dry-run-locked per W5-D1 until the
 * deliverability P0 clears).
 */

export type AnnouncementTone = 'info' | 'warning' | 'danger';
export type AnnouncementAudience = 'customers' | 'staff' | 'all';

export interface Announcement {
  id: string;
  title: string;
  body: string;
  tone: AnnouncementTone;
  audience: AnnouncementAudience;
  startsAt: string;
  endsAt: string | null;
  isActive: boolean;
  emailSentAt: string | null;
  createdAt: string;
}

interface Row {
  id: string;
  title: string;
  body: string;
  tone: AnnouncementTone;
  audience: AnnouncementAudience;
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
  email_sent_at: string | null;
  created_at: string;
}

const COLS = 'id, title, body, tone, audience, starts_at, ends_at, is_active, email_sent_at, created_at';

const mapRow = (r: Row): Announcement => ({
  id: r.id,
  title: r.title,
  body: r.body,
  tone: r.tone,
  audience: r.audience,
  startsAt: r.starts_at,
  endsAt: r.ends_at,
  isActive: r.is_active,
  emailSentAt: r.email_sent_at,
  createdAt: r.created_at,
});

export const announcementService = {
  /** Active-window rows for a surface (RLS trims to the live window). */
  async listActive(audience: 'customers' | 'staff'): Promise<Announcement[]> {
    const { data, error } = await supabase
      .from('announcement')
      .select(COLS)
      .in('audience', ['all', audience])
      .order('created_at', { ascending: false });
    if (error) throw new Error(`Could not load announcements: ${error.message}`);
    return ((data ?? []) as Row[]).map(mapRow);
  },

  /** Admin list — full staff see every row via the manage policy. */
  async listAll(): Promise<Announcement[]> {
    const { data, error } = await supabase
      .from('announcement')
      .select(COLS)
      .order('created_at', { ascending: false });
    if (error) throw new Error(`Could not load announcements: ${error.message}`);
    return ((data ?? []) as Row[]).map(mapRow);
  },

  async create(input: {
    title: string;
    body: string;
    tone: AnnouncementTone;
    audience: AnnouncementAudience;
    starts_at?: string;
    ends_at?: string | null;
    is_active?: boolean;
  }): Promise<void> {
    const { error } = await supabase.from('announcement').insert(input);
    if (error) throw new Error(`Could not create announcement: ${error.message}`);
  },

  async update(id: string, patch: Partial<{
    title: string;
    body: string;
    tone: AnnouncementTone;
    audience: AnnouncementAudience;
    starts_at: string;
    ends_at: string | null;
    is_active: boolean;
  }>): Promise<void> {
    const { error } = await supabase
      .from('announcement')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(`Could not update announcement: ${error.message}`);
  },

  /** Email blast via EF. dry_run must be EXPLICITLY false to really send
   *  (W5-D1 — hard gate until the deliverability P0 lands). */
  async sendBlast(id: string, dryRun: boolean): Promise<{
    ok: true; dry_run: boolean; target_count: number; enqueued: number; skipped: number;
  }> {
    const { data, error } = await supabase.functions.invoke('announcement-send', {
      body: { announcement_id: id, dry_run: dryRun },
    });
    if (error) {
      const ctx = (error as unknown as { context?: Response }).context;
      if (ctx && typeof ctx.clone === 'function') {
        try {
          const parsed = (await ctx.clone().json()) as { message?: string; code?: string };
          throw new Error(parsed.message || parsed.code || error.message);
        } catch (e) {
          if (e instanceof Error && e.message !== error.message) throw e;
        }
      }
      throw new Error(error.message);
    }
    return data as { ok: true; dry_run: boolean; target_count: number; enqueued: number; skipped: number };
  },
};
